import { CameraOverlayButton, HeaderPopup, Icons, LayoutType, MicrophoneOverlayButton, Popup, ReactIcons, StudentPage, StudentUI } from "@kalyzee/escaet-ui";
import { Outlet } from "react-router-dom";
import { PageMasterMode, usePageContext } from "../../../../components/navigation/PageContext";

import {
  generateRandomId,
  isRoleGreaterThan,
  isRoleGreaterThanOrEqual,
  isSpectator,
  MeetingSessionOvenMediaReceiver,
  MeetingSessionOvenMediaReceiverManagerBaseData,
  useMeetingAudioMixer
} from "@kalyzee/kast-web-sdk";
import {
  addComponentOnOverlay,
  audioContextIsReadyToUse,
  Button,
  Loading,
  MediaCaptureRef,
  removeComponentOnOverlay,
  Touchable,
  useOuterClickManyTargets,
  usePrevious,
  useRefAutoUpdate,
  useStateWithRef,
  Video
} from "@kalyzee/kast-web-sdk/web-components";
import { Chatroom, MeetingRoomStatus, MeetingSessionHighlightingState, MeetingSessionStatus, MeetingState, UserRole } from "@kalyzee/kast-web-sdk/websocket";
import React, { createRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import DashboardChat from "../../../../components/dashboard/Chat";
import { MediaSelector } from "../../../../components/media/MediaSelector";
import { toastError, toastSuccess } from "../../../../helpers/toast";
import { useMeetingConfigurator, useMeetingDataOnChange, useMeetingSelector, useMultiMeetingAPI } from "../../../../hooks/meeting";
import { SessionMode } from "../../../../store/session/slices";
import { useStudentContext } from "../context";

import { OvenMediaReceiverWebRTCSession, RTCStreamSenderParamsPreset } from "@kalyzee/kast-web-sdk/webrtc";
import { Highlighting } from "../../../../components/media/Highlighting";
import MeetingSessionMediaPlayer from "../../../../components/player/MeetingSessionMediaPlayer";
import { getExpertActor, isExpertVideoSession } from "../../../../helpers/meeting";

import PauseView from "../../../../components/utils/PauseView";
import { useSettings } from "../../../../hooks/settings";
import { useUser } from "../../../../store/session/hooks";

import { BottomPanelWebrtcSessionStats } from "../../../../components/debug/BottonPanelWebrtcSessionStats";
import { ScreenShare, ScreenShareImperativeAPI, ScreenShareStatus } from "../../../../components/media/ScreenShare";
import { MeetingActivityJoinPopup } from "../../../../components/meeting/MeetingActivityJoinPopup";
import { DevSoundIndicatorsBanner } from "../../../../components/utils/DevSoundIndicatorBanner";
import OverlayInformationMessage from "../../../../components/utils/OverlayInformationMessage";
import { useActivityData, useBroadcastingExpertSessions, usePopulatedMeetingData, useStudentAvailableViews } from "../../../../hooks/populatedData";

import { MeetingContainer, MeetingContainerAPI } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import SelfPreview from "../../../../components/media/SelfPreview";
import { PedagogyPopup } from "../../../../components/pedagogy/student/PedagogyPopup";
import { getAudioContextForAudioMixer, getAudioContextForVolumeAnalyzer } from "../../../../helpers/audio";
import { sdkClient } from "../../../../helpers/sdk";
import { DEFAULT_MAX_BITRATES } from "../../../../helpers/videos";
import { expertWebcamName, pedagogyWebcamName, studentWebcamName } from "../../../../helpers/webcam";
import { useScoreReporter } from "../../../../hooks/behaviors";
import styles from "./meeting.module.css";

const fromUIPage = (page: StudentPage) => {
  const mapping = {
    [StudentPage.Dashboard]: PageMasterMode.MEETING_DASHBOARD,
    [StudentPage.Group]: PageMasterMode.MEETING_GROUP,
    [StudentPage.Highlight]: PageMasterMode.MEETING_HIGHLIGHT,
    [StudentPage.Survey]: PageMasterMode.MEETING_SURVEY,
  };
  return mapping[page];
};

const toUIPage = (page?: PageMasterMode) => {
  if (page === PageMasterMode.MEETING_DASHBOARD) return StudentPage.Dashboard;
  if (page === PageMasterMode.MEETING_GROUP) return StudentPage.Group;
  if (page === PageMasterMode.MEETING_HIGHLIGHT) return StudentPage.Highlight;
  if (page === PageMasterMode.MEETING_SURVEY) return StudentPage.Survey;
  return StudentPage.Dashboard;
};

export const MeetingStudentPage = () => {
  const [settings] = useSettings();
  const settingsRef = useRefAutoUpdate(settings);

  const highlightMode = useRef(false);
  const surveyMode = useRef(false);

  const pageContext = usePageContext<SessionMode.MASTER>();
  const pageContextRef = useRefAutoUpdate(pageContext);
  const screenShareRef = useRef<ScreenShareImperativeAPI>(null);

  const [screenShareStatus, setScreenShareStatus] = useState<ScreenShareStatus>("idle");

  const selfUser = useUser();

  const [popupMode, setPopupMode, popupModeRef] = useStateWithRef<"none" | "media" | "screen" | "chat">("none");

  const multiMeetingAPI = useMultiMeetingAPI();
  const multiMeetingAPIRef = useRefAutoUpdate(multiMeetingAPI);

  // If a Peda Meeting API is available, we use it and it becomes the focusedMeetingAPI
  // Otherwise, we use the URL selected meeting API
  const { selectedMeetingAPI, pedagogy, focusedMeetingAPI } = useMultiMeetingAPI(
    pageContextRef.current?.params?.id,
    true,
    true // only selected if no other running
  );
  const focusedMeetingAPIRef = useRefAutoUpdate(focusedMeetingAPI);

  (window as any).focusedMeetingAPI = focusedMeetingAPI;
  useMeetingConfigurator(focusedMeetingAPI, {
    ovenMediaSession: {
      emission: {
        options: (mediaSession) => {
          if (mediaSession.name === "screen") {
            const maxBitrate = settingsRef.current?.debug?.maxBitrate?.student?.screensharing ?? DEFAULT_MAX_BITRATES.student.screensharing;
            return {
              rtc: {
                streamParams: {
                  preset: RTCStreamSenderParamsPreset.VISIO_SCREENSHARING,
                  video: { rtcEncoding: { maxBitrate: maxBitrate?.video } },
                  audio: { rtcEncoding: { maxBitrate: maxBitrate?.audio } },
                },
              },
            };
          }
          const maxBitrate = settingsRef.current?.debug?.maxBitrate?.student?.webcam ?? DEFAULT_MAX_BITRATES.student.webcam;
          return {
            rtc: {
              streamParams: {
                preset: RTCStreamSenderParamsPreset.VISIO_FACE,
                video: { rtcEncoding: { maxBitrate: maxBitrate?.video } },
                audio: { rtcEncoding: { maxBitrate: maxBitrate?.audio } },
              },
            },
          };
        },
      },
      reception: {
        options: (session) => {
          const isExpert = session.user?.role ? isRoleGreaterThanOrEqual(session.user?.role, UserRole.ORGANIZER) : false;
          return {
            video: isExpert ? true : { mode: "subscription", delayToDisableStream: 5000 }, // always get stream for expert
            audio: true,
          }
        },
      },
    },
  });

  const [meeting] = useMeetingSelector(focusedMeetingAPI, (state) => state.meeting);
  const [session, sessionRef] = useMeetingSelector(focusedMeetingAPI, (state) => state.session);
  const [rooms] = useMeetingSelector(focusedMeetingAPI, (state) => state.rooms);
  const [emissionSessions] = useMeetingSelector(focusedMeetingAPI, (state) => state.emissionSessions);
  const [receptionSessions] = useMeetingSelector(focusedMeetingAPI, (state) => state.receptionSessions);
  const [currentSurvey] = useMeetingSelector(focusedMeetingAPI, (state) => state.surveys?.[0]);

  const [selectedSession] = useMeetingSelector(selectedMeetingAPI, (state) => state.session);

  const studentCtx = useStudentContext();
  const studentCtxRef = useRefAutoUpdate(studentCtx);

  const mediaSelectorRef = useRef<MediaCaptureRef>();
  const [chatBadgeValue, setChatBadgeValue] = useState(0);

  const popupContainerRef = useRef<HTMLDivElement>(null);
  const chatButtonRef = useRef<HTMLButtonElement>(null);
  useOuterClickManyTargets([popupContainerRef, chatButtonRef as any], () => {
    if (popupModeRef.current === "chat") setPopupMode("none");
  });

  const [audioPermitted] = useMeetingSelector(focusedMeetingAPI, (state) => state.session?.permissions?.audio ?? true);

  useScoreReporter(focusedMeetingAPI);

  useMeetingDataOnChange(
    focusedMeetingAPI,
    (state) => state?.session?.permissions?.audio ?? true,
    (value, previous) => {
      if (value) toastSuccess("Vous pouvez à nouveau activer votre micro.");
      else toastError("L'activation du micro est désormais bloquée.");
    }
  );

  /**
   * Switching between paused and running when a pedagogy is available for us
   */
  useEffect(() => {
    if (!selectedSession) return;

    const status = selectedSession?.status;
    const emitter = sdkClient.getWebsocketClient().emitter;

    const waitedInPedagogy = !!pedagogy?.myRoom;
    if (waitedInPedagogy && status === MeetingSessionStatus.RUNNING) {
      // A peda meeting is available but we need to pause this meeting in
      selectedMeetingAPI?.updater.update(selectedSession.id, {
        status: MeetingSessionStatus.PAUSED,
      });
    }

    if (!waitedInPedagogy && status === MeetingSessionStatus.PAUSED) {
      // No peda meeting available, we can unpause the current meeting
      selectedMeetingAPI?.updater.update(selectedSession.id, {
        status: MeetingSessionStatus.RUNNING,
      });
    }
  }, [pedagogy, selectedMeetingAPI, selectedSession]);

  /**
   * If we are connected to a pedagogy meetign but we no longer
   * have a room waiting for us in it, we need to leave it
   */
  useEffect(() => {
    if (pedagogy && !pedagogy.myRoom) {
      pedagogy.container.getAPI().leave();
    }
  }, [pedagogy]);

  ////////////////////// Populated DATA //////////////////////

  const populatedMeetingData = usePopulatedMeetingData(focusedMeetingAPI);
  const activityData = useActivityData(populatedMeetingData);

  const availableViews = useStudentAvailableViews(populatedMeetingData, activityData, selfUser?.id, session?.id);
  const previousAvailableView = usePrevious(availableViews);

  const currentView = useMemo(() => {
    if (pageContext?.page === PageMasterMode.MEETING_HIGHLIGHT) {
      const highlightView = availableViews.find((view) => view.type === "highlight");
      if (highlightView) return highlightView;
    }
    return availableViews[availableViews.length - 1]; // TODO : there should be a default view, like this structure : { availableViews, defaultView, currentView }
  }, [availableViews, pageContext]);

  const broadcastingExpertSessions = useBroadcastingExpertSessions(populatedMeetingData);

  const expertNonWebcamVideoSessions = useMemo(
    () =>
      (currentView?.experts ?? [])
        .flatMap((data) => data.session.videoSessions)
        .filter((videoSession) => videoSession.data.mediaSession.name !== expertWebcamName),
    [currentView]
  );



  ////////////////////// Populated DATA //////////////////////

  useEffect(() => {
    const session = sessionRef.current;
    if (!session || !focusedMeetingAPIRef.current) return;
    const currHighlightView = availableViews.find((view) => view.type === "highlight");
    if (!currHighlightView) return;
    const previousHighlightView = previousAvailableView?.find((view) => view.type === "highlight");
    const myPreviousSessionData = previousHighlightView?.students.find((s) => s.video && s.session.id === session.id);
    const myCurrSessionData = currHighlightView.students.find((s) => s.video && s.session.id === session.id);

    if (!myPreviousSessionData && myCurrSessionData) {
      // start of the highlight
      if (!focusedMeetingAPIRef.current?.emissionManager.audioIsEnabled("cameras")) {
        focusedMeetingAPIRef.current?.unmute({ direction: "outgoing", group: "cameras" });
      }
    } else if (myPreviousSessionData && !myCurrSessionData) {
      // end of the highlight
      if (focusedMeetingAPIRef.current?.emissionManager.audioIsEnabled("cameras")) {
        focusedMeetingAPIRef.current?.mute({ direction: "outgoing", group: "cameras" });
      }
    }
  }, [availableViews, previousAvailableView]);

  const groupsAreActive = populatedMeetingData?.rooms?.groups.some((g) => g.room.status !== MeetingRoomStatus.Closed);

  const canHearSession = (_: MeetingContainerAPI, session: MeetingSessionOvenMediaReceiver) => {
    if (!currentView) return false;
    const sessionStatus = session.data.session?.status;
    const sessionId = session.data.session?.id;
    if (!sessionId) return false;
    if (sessionStatus !== MeetingSessionStatus.RUNNING) return false;

    const expertData = currentView.experts.find((data) => data.session.id === sessionId);
    if (expertData) {
      // Audio disabled for whole session
      if (!expertData.audio) return false;
      // Maybe audio needs to be disabled for this specific media session
      if (session.data.mediaSession.name === expertWebcamName) {
        // expert webcam is not visible in those modes
        return currentView.type !== "normal" && currentView.type !== "group";
      } else if (session.data.mediaSession.name === pedagogyWebcamName) {
        return currentView.type !== "group" && activityData?.mode !== "pedagogy";
      } else {
        // other devices than webcam can only be visible in normal mode
        return currentView.type === "normal";
      }
    }

    if (session.data.session.canBeHeardByEveryone) return true;

    const studentData = currentView.students.find((data) => data.session.id === sessionId);
    if (studentData) {
      return studentData.audio;
    }

    return false;
  };

  const audioMixer = useMeetingAudioMixer(getAudioContextForAudioMixer(), focusedMeetingAPI, true, canHearSession, settings.debug?.audio?.mixer);

  const myGroupStatus = populatedMeetingData?.me.group?.room.status;
  const canShareScreen = session?.highlighting === MeetingSessionHighlightingState.ONGOING || (myGroupStatus && myGroupStatus !== MeetingRoomStatus.Closed);

  // ---------------------------------------------------------------------- //
  // ---------------------------   UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  /**
   * Joins a container, not necessarily the current one
   */
  const joinContainer = async (container: MeetingContainer) => {
    if (container.getStatus() !== "disconnected") return;
    const containerAPI = container.getAPI();
    if (studentCtx) {
      if (studentCtx.audioStreamEnabled) await containerAPI.unmute({ direction: "outgoing", group: "cameras" });
      else await containerAPI.mute({ direction: "outgoing", group: "cameras" });
      if (studentCtx.videoStreamEnabled) await containerAPI.enableVideo({ direction: "outgoing", group: "cameras" });
      else await containerAPI.disableVideo({ direction: "outgoing", group: "cameras" });
      studentCtx.setAudioStreamEnabled(containerAPI.emissionManager.audioIsEnabled("cameras"));
      studentCtx.setVideoStreamEnabled(containerAPI.emissionManager.videoIsEnabled("cameras"));
    }

    const type = "camera";
    const group = "cameras";
    const response = await containerAPI.join({
      medias: [
        {
          video: {
            deviceType: "camera",
            enabled: containerAPI.emissionManager.videoIsEnabled(group),
          },
          audio: {
            deviceType: "audio",
            enabled: containerAPI.emissionManager.audioIsEnabled(group),
          },
          name: studentWebcamName,
          extra: {
            type,
          },
        },
      ],
    });

    if (response.error) {
      console.error(`[ERROR] join: `, response.error);
      toastError(`Une erreur est apparue.`);
      pageContextRef.current?.goTo(PageMasterMode.LIST);
      container.destroy();
      return;
    }
    const session = response.response?.session;
    if (!session) {
      console.error(`[ERROR] join - internal error: session is undefined`);
      toastError(`Une erreur est apparue.`);
      pageContextRef.current?.goTo(PageMasterMode.LIST);
      container.destroy();
      return;
    }
  };

  const createAndJoinContainer = async (id: string) => {
    const api = multiMeetingAPIRef.current;

    if (!api.all) {
      console.error(`[ERROR] join - meetingContext is undefined `);
      toastError(`Une erreur est apparue.`);
      pageContextRef.current?.goTo(PageMasterMode.LIST);
      return;
    }

    const container = api.all.getOrCreateContainer(id);
    joinContainer(container);
  };

  const leave = async () => {
    // leave all
    const promises: Promise<any>[] = [];
    multiMeetingAPIRef.current?.all?.getContainers()?.forEach((c) => {
      const result = c.getAPI()?.leave();
      if (!("error" in result)) promises.push(result);
    });
    await Promise.allSettled(promises);
  };

  const bindWebcamMedia = async (mediaStream: MediaStream) => {
    studentCtx?.setSelfVideoStream?.(mediaStream);

    const session = sessionRef.current;
    if (!session) return;
    const media = session.medias.find((m) => m.name === studentWebcamName);
    if (!media) return;

    focusedMeetingAPIRef.current?.setMediaStream(media.id, mediaStream);
  };

  const updateMedias = useCallback(async () => {
    const meetingContainerAPI = focusedMeetingAPIRef.current;
    if (!meetingContainerAPI) return;
    const manager = meetingContainerAPI.emissionManager;
    if (session) {
      let camerasVideoIsEnabled = true;
      let camerasAudioIsEnabled = true;
      session.medias?.forEach((m) => {
        if (m.extra?.type === "camera" || m.extra?.type === undefined) camerasVideoIsEnabled = camerasVideoIsEnabled && m.video.enabled;
        if (m.extra?.type === "camera" || m.extra?.type === undefined) camerasAudioIsEnabled = camerasAudioIsEnabled && m.audio.enabled;
      });
      if (!camerasVideoIsEnabled && manager.videoIsEnabled("cameras")) {
        await meetingContainerAPI.disableVideo({ direction: "outgoing", group: "cameras" });
      } else if (camerasVideoIsEnabled && !manager.videoIsEnabled("cameras")) {
        await meetingContainerAPI.enableVideo({ direction: "outgoing", group: "cameras" });
      }
      if (!camerasAudioIsEnabled && manager.audioIsEnabled("cameras")) {
        await meetingContainerAPI.mute({ direction: "outgoing", group: "cameras" });
      } else if (camerasAudioIsEnabled && !manager.audioIsEnabled("cameras")) {
        await meetingContainerAPI.unmute({ direction: "outgoing", group: "cameras" });
      }
    }

    const state = meeting?.state;
    if (state === MeetingState.PAUSED) {
      audioMixer?.suspend().catch((err) => console.error("Error suspend audio : ", err));
    } else {
      audioMixer?.resume().catch((err) => console.error("Error resume audio : ", err));
    }

    studentCtxRef.current?.setVideoStreamEnabled(manager.videoIsEnabled("cameras"));
    studentCtxRef.current?.setAudioStreamEnabled(manager.audioIsEnabled("cameras"));
  }, [studentCtxRef, session, meeting]);

  const onLayoutChange = (type: LayoutType) => {
    studentCtx?.setLayout?.(type);
  };

  const onPageChange = (page: StudentPage) => {
    pageContextRef.current?.goTo(fromUIPage(page), { id: pageContextRef.current?.params?.id });
  };

  // ---------------------------------------------------------------------- //
  // --------------------------- EFFECTS ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    const containers = multiMeetingAPIRef.current.all?.getContainers();
    containers?.forEach((container) => {
      joinContainer(container);
    });
  }, [multiMeetingAPI.all]);

  useEffect(() => {
    if (!canShareScreen) {
      screenShareRef.current?.stop();
    }
  }, [canShareScreen]);

  useEffect(() => {
    if (screenShareStatus === "idle" && popupMode === "screen") {
      setPopupMode("none");
    }
  }, [screenShareStatus, popupMode]);

  useEffect(() => {
    studentCtxRef.current?.setActivityData(activityData);
  }, [activityData]);
  useEffect(() => {
    studentCtxRef.current?.setPopulatedMeetingData?.(populatedMeetingData);
  }, [populatedMeetingData]);
  useEffect(() => {
    studentCtxRef.current?.setAvailableViews?.(availableViews);
  }, [availableViews]);
  useEffect(() => {
    studentCtxRef.current?.setCurrentView?.(currentView);
  }, [currentView]);

  const haveActiveGroups = useMemo(() => populatedMeetingData?.rooms?.groups.some((g) => g.room.status !== MeetingRoomStatus.Closed), [populatedMeetingData]);

  useEffect(() => {
    // Not ready to use, we cannot decide if a redirection is needed
    if (!meeting) return;

    // I am in a pedagogy meeting
    if (meeting.specializedData?.isPedagogy) {
      if (pageContextRef.current?.page === PageMasterMode.MEETING_PEDAGOGY) {
        return;
      }
      // I keep the ID of my current meeting, this will be used as an origin meeting
      // => to know what meeting i'm coming from
      pageContextRef.current?.goTo(PageMasterMode.MEETING_PEDAGOGY, { id: pageContextRef.current?.params?.id });
      return;
    }

    if (currentSurvey) {
      // already on page
      if (pageContextRef.current?.page === PageMasterMode.MEETING_SURVEY) {
        return;
      }
      if (surveyMode.current) {
        // We already saw the last survey
        return;
      }
      surveyMode.current = true;
      pageContextRef.current?.goTo(PageMasterMode.MEETING_SURVEY, { id: pageContextRef.current?.params?.id });
      return;
    }
    surveyMode.current = false;

    // Priority to highllighted content
    if (availableViews.find((view) => view.type === "highlight")) {
      if (highlightMode.current) {
        // We already had highlighted sessions (or were highlighted ourself) before this update
        // Maybe we are on the hightlight page, maybe not, does not matter
        return;
      }
      pageContextRef.current?.goTo(PageMasterMode.MEETING_HIGHLIGHT, { id: pageContextRef.current?.params?.id });
      highlightMode.current = true;
      return;
    }
    highlightMode.current = false;

    // Nothing is highlighted, maybe we have active groups
    if (haveActiveGroups) {
      if (pageContextRef.current?.page === PageMasterMode.MEETING_GROUP) {
        return;
      }
      pageContextRef.current?.goTo(PageMasterMode.MEETING_GROUP, { id: pageContextRef.current?.params?.id });
      return;
    }

    // Nothing is highlighted, nothing is active, we can go back to the dashboard
    if (pageContextRef.current?.page !== PageMasterMode.MEETING_DASHBOARD) {
      pageContextRef.current?.goTo(PageMasterMode.MEETING_DASHBOARD, { id: pageContextRef.current?.params?.id });
    }
  }, [availableViews, pageContextRef, populatedMeetingData, currentSurvey, haveActiveGroups]);

  useEffect(() => {
    if (!studentCtx) return;
    if (!mediaSelectorRef.current) return;
    mediaSelectorRef.current.enableAudio(studentCtx.audioStreamEnabled);
    mediaSelectorRef.current.enableVideo(studentCtx.videoStreamEnabled);
  }, [studentCtx]);

  useEffect(() => {
    if (!studentCtx) return;
    const info = studentCtx.outputAudioDeviceInfo;
    if (info && audioMixer?.isRunning()) {
      audioMixer.setAudioOutputDevice(info).catch(() => {
        console.error(`audio device (${JSON.stringify(info)}) is invalid`);
      });
    }
  }, [audioMixer, studentCtx?.outputAudioDeviceInfo]);

  useEffect(() => {
    const id = pageContextRef.current?.params?.id ?? "";
    audioContextIsReadyToUse(getAudioContextForVolumeAnalyzer()).then((ready) => {
      if (!ready) {
        const queries: any = {};
        if (id) queries["meeting"] = id;
        pageContextRef.current?.goTo(PageMasterMode.LIST, undefined, { queries });
      } else {
        createAndJoinContainer(id);
      }
    });
    return () => {
      leave();
    };
  }, []);

  useEffect(() => {
    let currentPopup: React.ReactNode;
    const showMeetingNotReadyPopup = () => {
      if (currentPopup) {
        removeComponentOnOverlay(currentPopup);
        currentPopup = undefined;
      }
      currentPopup = (
        <Popup
          Header={<HeaderPopup icon={Icons.TimerIcon} title="Le cours va bientôt commencer, veuillez patienter" />}
          Content={<Loading style={{ width: "100%", "--loading-svg-color": "black" } as any} />}
          style={{
            width: "fit-content",
            height: "fit-content",
            padding: "2em",
          }}
        />
      );
      addComponentOnOverlay(currentPopup, `meeting_state_not_ready_${generateRandomId()}`);
    };

    const state = meeting?.state;
    if (state === MeetingState.IDLE) {
      showMeetingNotReadyPopup();
    } else {
      if (currentPopup) {
        removeComponentOnOverlay(currentPopup);
        currentPopup = undefined;
      }
    }

    if (state === MeetingState.IDLE) showMeetingNotReadyPopup();

    return () => {
      if (currentPopup) {
        removeComponentOnOverlay(currentPopup);
        currentPopup = undefined;
      }
    };
  }, [meeting?.state]);

  useEffect(() => {
    emissionSessions?.forEach((s) => {
      const type = s.data.mediaSession.extra?.type;
      const group = type === "camera" ? "cameras" : type === "screen" ? "screens" : "undefined";
      if (group) s.data.groups = [group];
    });
  }, [emissionSessions]);

  useEffect(() => {
    receptionSessions?.forEach((s) => {
      s.data.extra = {
        video: isExpertVideoSession(meeting, s),
        audio: true,
      };
    });
  }, [receptionSessions, meeting]);

  useEffect(() => {
    const meetingId = meeting?.id;
    if (!meetingId) return;
    const ids = studentCtx?.getMeetingOptions(meetingId)?.enabledVideoSessionIds ?? [];
    const toKeepIds = ids.filter((id) => receptionSessions?.find((s) => s.data.mediaSession.id === id));
    if (ids.length !== toKeepIds.length) {
      studentCtx?.updateMeetingOptions(meetingId, {
        enabledVideoSessionIds: toKeepIds,
      });
    }
    if (studentCtx?.selfVideoStream) bindWebcamMedia(studentCtx?.selfVideoStream);
  }, [studentCtx, focusedMeetingAPI]);

  useEffect(() => {
    updateMedias();
  }, [updateMedias]);

  useEffect(() => {
    if (settings.debug?.audio?.mixer?.public) (window as any).audioMixer = audioMixer;
    if (settings.debug?.meetingContext?.public) (window as any).meetingContext = focusedMeetingAPI;
  }, [settings, audioMixer, focusedMeetingAPI]);

  if (!meeting || !session || !rooms || !selfUser || !studentCtx || !focusedMeetingAPI) return <Loading fullscreen message="Chargement en cours" />;

  const meetingOptions = studentCtx.getMeetingOptions(meeting.id) ?? {};

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER --------------------------------- //
  // ---------------------------------------------------------------------- //

  const onClickPlayer = (sessionId: string) => {
    const selectedSessions = meetingOptions.enabledVideoSessionIds ?? [];
    const index = selectedSessions.indexOf(sessionId);
    if (index === -1) {
      studentCtx.updateMeetingOptions(meeting.id, {
        enabledVideoSessionIds: [...selectedSessions, sessionId],
      });
    } else {
      studentCtx.updateMeetingOptions(meeting.id, {
        enabledVideoSessionIds: selectedSessions.filter((s) => s !== sessionId),
      });
    }
  };

  const renderPlayer = (session: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>) => {
    const mediaSessionId = session.data.mediaSession.id;
    const isSelected = studentCtx?.getMeetingOptions(meeting.id)?.enabledVideoSessionIds?.includes(mediaSessionId);
    const username = session.data.session.user?.username ?? "";
    const label = session.data.mediaSession.extra?.label ?? username;
    return (
      <Touchable
        key={`ovenmedia-session-${mediaSessionId}-drawer-player-container`}
        onPress={() => onClickPlayer(mediaSessionId)}
        className={[styles.drawerPlayerContainer, isSelected ? styles.selected : ""].join(" ")}
      >
        <MeetingSessionMediaPlayer
          className={styles.drawerPlayer}
          meeting={meeting}
          sessionId={session.data.session.id}
          mediaSessionId={mediaSessionId}
          ovenMediaSession={session}
          muted={true}
        />
        <div className={styles.drawerPlayerFooter}>{`${label}${
          session.data.session.highlighting === MeetingSessionHighlightingState.ONGOING ? " (Au tableau)" : ""
        }`}</div>
      </Touchable>
    );
  };

  const renderAllSelfPreviews = () => {
    if (isSpectator(session)) return null;
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "0.5em",
        }}
      >
        {screenShareStatus !== "idle" && (
          <SelfPreview
            color={popupMode === "screen" ? "#888888" : "#555555"}
            stream={studentCtx.selfScreenStream}
            icon={
              <ReactIcons.SharedScreen
                style={{
                  color: "#3BD2C9",
                  width: 28,
                  height: 28,
                }}
              />
            }
            onClick={() => setPopupMode((v) => (v === "screen" ? "none" : "screen"))}
          />
        )}
        <SelfPreview
          color={popupMode === "media" ? "#888888" : "#555555"}
          stream={studentCtx.selfVideoStream}
          icon={<ReactIcons.Settings2 fill={popupMode === "media" ? "#CCCCCC" : "#999999"} width={25} height={25} />}
          onClick={() => setPopupMode((v) => (v === "media" ? "none" : "media"))}
        />
      </div>
    );
  };

  const renderPlayersDrawerContent = () => {
    return expertNonWebcamVideoSessions.map((session) => renderPlayer(session));
  };

  const expertUser = getExpertActor(meeting)?.user;

  const renderUIBottomRightDock = () => {
    let currentChatroom: Chatroom | undefined;
    if (currentView.selfGroup) {
      // I am in a group -> i use the group chatroom
      currentChatroom = currentView.selfGroup.room.chatrooms?.[0];
    } else {
      currentChatroom = meeting.chatrooms?.[0];
    }
    return (
      <div
        ref={popupContainerRef}
        style={{
          position: "absolute",
          right: 0,
          bottom: 0,
          zIndex: 5,
          margin: "1em",
        }}
      >
        {/* This component has to stay mounted */}
        {!isSpectator(session) ? (
          <MediaSelector
            ref={mediaSelectorRef}
            display={popupMode === "media"}
            style={{
              width: "500px",
              height: "400px",
            }}
            quality={session?.highlighting === MeetingSessionHighlightingState.ONGOING ? "high" : "low"}
            audioStreamEnabled={studentCtx.audioStreamEnabled}
            videoStreamEnabled={studentCtx.videoStreamEnabled}
            defaultInputAudioDeviceInfo={studentCtx.inputAudioDeviceInfo}
            defaultInputVideoDeviceInfo={studentCtx.inputVideoDeviceInfo}
            defaultOutputAudioDeviceInfo={studentCtx.outputAudioDeviceInfo}
            onInputAudioDeviceInfo={(device) => {
              if (device) studentCtx.setInputAudioDeviceInfo?.(device);
            }}
            onInputVideoDeviceInfo={(device) => {
              if (device) studentCtx.setInputVideoDeviceInfo?.(device);
            }}
            onOutputAudioDeviceInfo={(device) => {
              if (device) studentCtx.setOutputAudioDeviceInfo?.(device);
            }}
            onMediaStream={bindWebcamMedia}
            onMediaCaptureError={(type, error) => {
              if (type === "audio") {
                toastError("Veuillez autoriser le navigateur à accéder au microphone");
                focusedMeetingAPIRef.current?.mute({ direction: "outgoing", group: "cameras" });
              }
              if (type === "video") {
                toastError("Veuillez autoriser le navigateur à accéder à la caméra");
                focusedMeetingAPIRef.current?.disableVideo({ direction: "outgoing", group: "cameras" })
              }
            }}
          />
        ) : null}
        {popupMode === "screen" ? (
          <div
            style={{
              width: 500,
              borderRadius: 6,
              padding: 4,
              backgroundColor: "#555555",
            }}
          >
            <Video autoPlay muted style={{ width: "100%", height: "100%", borderRadius: 6, objectFit: "contain" }} src={studentCtx.selfScreenStream} />
          </div>
        ) : null}
        {currentChatroom && (
          <DashboardChat
            chatroom={currentChatroom}
            style={{ width: 500, height: 400, display: popupMode === "chat" ? "flex" : "none" }}
            meetingContainer={focusedMeetingAPI}
            className={styles.chatContainer}
            hideActiveChatButton
            hideClearChatButton
            inactive={popupMode !== "chat"}
            onUnreadMessages={(messages) => {
              setChatBadgeValue(messages.length);
            }}
            disableDeletion={false}
            disableReactions={false}
          />
        )}
      </div>
    );
  };

  const getEnabledUIPages = () => {
    const pages: StudentPage[] = [];
    availableViews.forEach((view) => {
      switch (view.type) {
        case "group":
        case "activity_all_leaders":
        case "activity_one_group":
        case "activity_one_leader":
          pages.push(StudentPage.Group);
          break;
        case "normal":
          pages.push(StudentPage.Dashboard);
          break;
        case "highlight":
          pages.push(StudentPage.Highlight);
          break;
      }
    });
    if (currentSurvey) {
      pages.push(StudentPage.Survey);
    }
    return pages;
  };

  const renderUI = (children: React.ReactNode) => {
    const buttons = [];
    const renderCameraButton = () => {
      const permitted = !(session.permissions?.video === false);
      const currRef = createRef<HTMLDivElement>();
      let hoverMessage: string | undefined = undefined;
      if (!permitted) hoverMessage = "Vous ne pouvez actuellement pas activer votre caméra";
      return (
        <div ref={currRef} key="student-dashboard-overlay-camera-btn">
          <CameraOverlayButton
            off={!studentCtx.videoStreamEnabled}
            onClick={async () => {
              if (!focusedMeetingAPI) return;
              const manager = focusedMeetingAPI.emissionManager;
              let success = true;
              if (manager.videoIsEnabled("cameras")) {
                success = (await focusedMeetingAPI.disableVideo({ direction: "outgoing", group: "cameras" })).success;
              } else {
                success = (await focusedMeetingAPI.enableVideo({ direction: "outgoing", group: "cameras" })).success;
              }
              if (!success) toastError("Une erreur est survenue...");
              studentCtx.setVideoStreamEnabled(manager.videoIsEnabled("cameras"));
            }}
            disabled={!permitted}
          />
          {hoverMessage ? <OverlayInformationMessage targetRef={currRef} message={hoverMessage} /> : null}
        </div>
      );
    };
    const renderAudioButton = () => {
      const currRef = createRef<HTMLDivElement>();
      let hoverMessage: string | undefined = undefined;
      if (!audioPermitted) hoverMessage = "Vous ne pouvez actuellement pas activer votre micro";
      return (
        <div ref={currRef} key="student-dashboard-overlay-microphone-btn">
          <MicrophoneOverlayButton
            off={!studentCtx.audioStreamEnabled}
            onClick={async () => {
              if (!focusedMeetingAPI) return;
              const manager = focusedMeetingAPI.emissionManager;
              let success = true;
              if (manager.audioIsEnabled("cameras")) {
                success = (await focusedMeetingAPI.mute({ direction: "outgoing", group: "cameras" })).success;
              } else {
                success = (await focusedMeetingAPI.unmute({ direction: "outgoing", group: "cameras" })).success;
              }
              if (!success) toastError("Une erreur est survenue...");
              studentCtx.setAudioStreamEnabled(manager.audioIsEnabled("cameras"));
            }}
            disabled={!audioPermitted}
          />
          {hoverMessage ? <OverlayInformationMessage targetRef={currRef} message={hoverMessage} /> : null}
        </div>
      );
    };
    const renderScreenShareButton = () => {
      return (
        (canShareScreen || screenShareStatus !== "idle") && (
          <ScreenShare
            meetingContainerAPI={focusedMeetingAPI}
            ref={screenShareRef}
            key="student-dashboard-overlay-screen-share-btn"
            onStatus={setScreenShareStatus}
            onStream={(stream) => studentCtx.setSelfScreenStream?.(stream)}
          />
        )
      );
    };
    if (!isSpectator(session)) {
      buttons.push(renderCameraButton());
      buttons.push(renderAudioButton());
      if (!groupsAreActive) {
        buttons.push(<Highlighting key="student-dashboard-overlay-highlighting-btn" />);
      }
      buttons.push(renderScreenShareButton());
    }
    const splitted = meeting?.name?.split("-") ?? [];
    const courseName = splitted.length > 1 ? splitted[0].trim() : "";
    return (
      <StudentUI
        screens={renderPlayersDrawerContent()}
        title={meeting?.name ?? ""}
        date={new Date(meeting?.startedAt ?? new Date())}
        studentUser={{
          courseName,
          firstName: selfUser.identity?.firstname ?? "",
          lastName: selfUser.identity?.lastname ?? "",
          profilePicture: selfUser.identity?.avatar,
        }}
        expertUser={{
          courseName,
          firstName: expertUser?.firstname ?? "",
          lastName: expertUser?.lastname ?? "",
          profilePicture: expertUser?.avatar,
        }}
        selectedLayout={studentCtx.layout}
        selectedPage={toUIPage(pageContextRef.current?.page)}
        onLayoutChange={onLayoutChange}
        onPageChange={onPageChange}
        onQuit={() => pageContextRef.current?.goTo(PageMasterMode.LIST)}
        onChatClick={() => {
          setPopupMode(popupModeRef.current === "chat" ? "none" : "chat");
        }}
        chatButtonRef={chatButtonRef}
        onOpenSettings={() => {}}
        buttonsCenter={buttons}
        placeholderBottomRight={renderAllSelfPreviews()}
        dockedContentBottomRight={renderUIBottomRightDock()}
        enabledLayouts={[LayoutType.Square, LayoutType.HorizontalSquare, LayoutType.VerticalSquare]}
        enabledPages={getEnabledUIPages()}
        chatBadgeValue={chatBadgeValue}
        disableSettingsButton
        forceOpenBottomBar
      >
        {children}
      </StudentUI>
    );
  };

  if (meeting.state === MeetingState.STOPPED) {
    return (
      <div className={styles.mainContainer}>
        <div className={styles.content} style={{ justifyContent: "center", alignItems: "center" }}>
          <div style={{ color: "white", marginBottom: "20px", fontSize: "1.5rem", fontWeight: "bold" }}>Le cours est terminé</div>
          <Button
            style={{ backgroundColor: "#0090FF", color: "white" }}
            title={"Retour à l'accueil"}
            onPress={() => {
              pageContextRef.current?.goTo(PageMasterMode.LIST);
            }}
          />
        </div>
      </div>
    );
  }

  const renderHighlightBanner = () => {
    const highlightView = availableViews.find((view) => view.type === "highlight");

    if (!highlightView || !session) {
      return null;
    }

    const mySessionData = highlightView.students.find((s) => s.video && s.session.id === session.id);

    let otherGroupsNames = highlightView.students
      .filter(
        (data) => data.video && data.session.user.group?.room.status === MeetingRoomStatus.Ongoing && data.session.user.group.id !== mySessionData?.session.user.group?.id
      )
      .map((data) => data.session.user.group?.room.name);
    otherGroupsNames = Array.from(new Set(otherGroupsNames));

    let you = false;
    let plural = false;

    let message = "";

    if (mySessionData) {
      if (mySessionData.session.user.group?.room.status === MeetingRoomStatus.Ongoing) {
        message = "Votre groupe";
      } else {
        message = "Vous";
        you = true;
      }
    }

    /**
     * Either :
     * - there is atleast one group and we display group names only, ignoring users
     * - there is no group and we display the users number
     */
    if (otherGroupsNames.length === 1) {
      if (message) {
        message += " et le groupe ";
        plural = true;
      } else {
        message = "Le groupe ";
      }
      message += otherGroupsNames[0];
    } else if (otherGroupsNames.length > 1) {
      if (message) {
        message += " et les groupes ";
      } else {
        message = "Les groupes ";
      }
      message += otherGroupsNames.join(", ");
    } else {
      // no other groups than mine

      // users that are not in the same room as me
      const otherSessions = highlightView.students.filter(
        (s) => 
          s.video 
          && s.session.id !== mySessionData?.session.id 
          // we discard the users from my group if it's ongoing
          && (
            mySessionData?.session.user.group?.room.status !== MeetingRoomStatus.Ongoing 
            || s.session.user.group?.id !== mySessionData?.session.user.group?.id
          )
      );
      const remaining = otherSessions.length;

      if (remaining === 1) {
        if (message) {
          message += " et 1 autre utilisateur";
          plural = true;
        } else {
          message = "1 utilisateur";
        }
      } else if (remaining > 1) {
        plural = true;
        if (message) {
          message += ` et ${remaining} autres utilisateurs`;
        } else {
          message = `${remaining} utilisateurs`;
        }
      }
    }

    if (message) {
      if (you) {
        message += " êtes au tableau";
      } else {
        if (plural) {
          message += " sont au tableau";
        } else {
          message += " est au tableau";
        }
      }
    } else {
      message = "Préparation du tableau ...";
    }

    const isOnHighlightPage = pageContextRef.current?.page === PageMasterMode.MEETING_HIGHLIGHT;

    return (
      <div
        onClick={() => {
          if (!isOnHighlightPage) {
            pageContextRef.current?.goTo(PageMasterMode.MEETING_HIGHLIGHT, { id: pageContextRef.current?.params?.id });
          }
        }}
        style={{
          display: "flex",
          gap: "1em",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          backgroundColor: "#0090FF",
          opacity: isOnHighlightPage ? 1 : 0.5,
          cursor: isOnHighlightPage ? "default" : "pointer",
          padding: "0.5em",
        }}
      >
        <div
          style={{
            color: "white",
            fontWeight: "bold",
          }}
        >
          {message.toUpperCase()}
        </div>
        {isOnHighlightPage || (
          <div
            style={{
              color: "white",
            }}
          >
            (Revenir)
          </div>
        )}
      </div>
    );
  };

  const renderActivityPopup = () =>
    activityData && session && <MeetingActivityJoinPopup activityData={activityData} meetingId={meeting.id} selfSessionId={session.id} />;

  const renderBroadcastingExpertBanner = () => {
    if (!broadcastingExpertSessions.length) return null;
    let expertSessions = 0;
    let pedagogySessions = 0;
    broadcastingExpertSessions.forEach((s) => {
      if (s.session?.extra?.isPedagogy) pedagogySessions++;
      else expertSessions++;
    });
    let message = "";
    if (pedagogySessions) {
      if (pedagogySessions > 1) {
        message = "Des ingénieurs pédagogiques";
      } else {
        message = "Un ingénieur pédagogique";
      }
    }
    if (expertSessions) {
      if (message.length) message += " et ";
      if (expertSessions > 1) {
        message += `${message.length ? "des" : "Des"} experts`;
      } else {
        message += `${message.length ? "un" : "Un"} expert`;
      }
    }
    message += ` ${broadcastingExpertSessions.length > 1 ? "s'adressent" : "s'adresse"} à tout le monde`;
    return (
      <div
        className={styles.broadcastingExpertIndicator}
        style={{
          backgroundColor: "#FF0000",
          padding: "0.1em 3em",
          fontSize: "1.25em",
          fontWeight: 500,
          color: "white",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "0.25em",
          whiteSpace: "nowrap",
        }}
      >
        <ReactIcons.PedagoHeadphone2 style={{ height: "1em" }} />
        {message}
      </div>
    );
  };

  const renderDebugOutgoingStream = () => {
    if (!settings?.debug?.displayOutgoingStreamStats && !settings?.debug?.displaySessionIds) return null;
    return (
      <BottomPanelWebrtcSessionStats
        incomingSessions={settings?.debug?.displayIncomingStreamStats ? receptionSessions : undefined}
        outgoingSessions={settings?.debug?.displayOutgoingStreamStats ? emissionSessions : undefined}
        sessions={(emissionSessions ?? []).map((s, i) => ({
          id: s.getId(),
          name: `${s.data?.mediaSession?.name ?? `ouput n°${i}`}`,
          session: settings?.debug?.displayOutgoingStreamStats ? s : undefined,
          meeting: settings?.debug?.displaySessionIds ? { session: session, mediaSession: s.data?.mediaSession } : undefined,
        }))}
      />
    );
  };

  return (
    <div className={styles.mainContainer}>
      {isSpectator(session) ? <div className={styles.spectatorBar}>Spectateur</div> : null}
      <div className={styles.content}>
        {renderUI(
          // TODO : should also stop sending stuff on backend side when paused
          // right now the UI is still updated in various ways because of backend events
          session.status === MeetingSessionStatus.RUNNING ? (
            <div
              style={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                width: "100%",
                height: "100%",
              }}
            >
              {renderHighlightBanner()}
              <div style={{ flex: 1, minHeight: 0, width: "100%" }}>
                <Outlet />
              </div>
              {renderActivityPopup()}
              {renderBroadcastingExpertBanner()}
              <DevSoundIndicatorsBanner meetingData={populatedMeetingData} canHearSession={(session) => canHearSession(focusedMeetingAPI, session)} />
            </div>
          ) : null
        )}
      </div>
      {renderDebugOutgoingStream()}
      <PedagogyPopup
        originContainer={selectedMeetingAPI}
        pedagogyContainer={pedagogy?.container?.getAPI()}
        pedagogyIsHere={currentView.type === "pedagogy_room" && !!currentView.experts.length}
      />
      {meeting?.state === MeetingState.PAUSED ? <PauseView meeting={meeting} /> : null}
    </div>
  );
};
